import React, { isValidElement } from 'react';
import { isFunction } from './assertion.esm.js';

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Creates a named context, provider, and hook.
 *
 * @param options create context options
 */
function createContext() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var context = arguments.length > 1 ? arguments[1] : undefined;
  var _options$strict = options.strict,
    strict = _options$strict === void 0 ? true : _options$strict,
    _options$errorMessage = options.errorMessage,
    errorMessage = _options$errorMessage === void 0 ? 'useContext: `context` is undefined. Seems you forgot to wrap component within the Provider' : _options$errorMessage,
    name = options.name;
  var Context = /*#__PURE__*/React.createContext(context);
  Context.displayName = name;
  function useContext() {
    var context = React.useContext(Context);
    if (!context && strict) {
      throw new Error(errorMessage);
    }
    return context;
  }
  return [Context.Provider, useContext, Context];
}

/**
 * Gets only the valid children of a component,
 * and ignores any nullish or falsy child.
 *
 * @param children the children
 */
function getValidChildren(children) {
  return React.Children.toArray(children).filter(function (child) {
    return /*#__PURE__*/React.isValidElement(child);
  });
}
/**
 * Assigns a value to a ref function or object
 *
 * @param ref the ref to assign to
 * @param value the value
 */
function assignRef(ref, value) {
  if (ref == null) return;
  if (isFunction(ref)) {
    ref(value);
    return;
  }
  try {
    // @ts-ignore
    ref.current = value;
  } catch (error) {
    throw new Error("Cannot assign value '".concat(value, "' to ref '").concat(ref, "'"));
  }
}

/**
 * Combine multiple React refs into a single ref function.
 * This is used mostly when you need to allow consumers forward refs to
 * internal components
 *
 * @param refs refs to assign to value to
 */
function mergeRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  return function (value) {
    refs.forEach(function (ref) {
      return assignRef(ref, value);
    });
  };
}
function getMergeRefs(setIsMounted) {
  return function () {
    for (var _len2 = arguments.length, refs = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      refs[_key2] = arguments[_key2];
    }
    return function (value) {
      refs.forEach(function (ref) {
        return assignRef(ref, value);
      });
      setIsMounted(true);
    };
  };
}
function hasRef(subject) {
  return !!subject && subject.hasOwnProperty('ref');
}
var reactNodeToString = function reactNodeToString(reactNode) {
  var string = '';
  if (typeof reactNode === 'string') {
    string = reactNode;
  } else if (typeof reactNode === 'number') {
    string = reactNode.toString();
  } else if (reactNode instanceof Array) {
    reactNode.forEach(function (child) {
      string += reactNodeToString(child);
    });
  } else if (/*#__PURE__*/isValidElement(reactNode)) {
    string += reactNodeToString(reactNode.props.children);
  }
  return string;
};

export { assignRef, createContext, getMergeRefs, getValidChildren, hasRef, mergeRefs, reactNodeToString };
//# sourceMappingURL=react-helpers.esm.js.map
